import React from "react";
import { BookingLabelsEnum } from "../../../../../../types/bookingEnums";
import { VenueConfig } from "../../../../../../types/venues";
import { VenueGroupConfig } from "../../../../../../types/VenueGroup";
import {
  getAllBookingLabels,
  getAllCustomerLabels
} from "../../../SelectedBooking/Labels/BookingLabels";
import { PillsWithOverflow } from "./PillsWithOverflow";

interface BookingLabelsProps {
  booking: any;
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig;
  maxPills?: number;
  isOnRunSheet?: boolean;
}

const isLightThemeActive = (): boolean =>
  document.documentElement.getAttribute("data-theme") === "Light";

const isYellowRange = (colourValue: number): boolean =>
  colourValue > 0xffcc00 && colourValue < 0xffff99;

const getDefaultLabelColour = (
  labelId: string,
  isLightTheme: boolean
): { backgroundColour: string; labelColour: string } => {
  const defaults: Record<
    string,
    { light: { bg: string; fg: string }; dark: { bg: string; fg: string } }
  > = {
    auto_confirmed: {
      light: { bg: "#a7f3d0", fg: "#065f46" },
      dark: { bg: "#065f46", fg: "#a7f3d0" }
    },
    linked_booking: {
      light: { bg: "#bfdbfe", fg: "#1e40af" },
      dark: { bg: "#1e40af", fg: "#bfdbfe" }
    }
  };

  const preset = defaults[labelId];
  if (preset) {
    const theme = isLightTheme ? preset.light : preset.dark;
    return { backgroundColour: theme.bg, labelColour: theme.fg };
  }

  return {
    backgroundColour: isLightTheme ? "#e5e7eb" : "#4b5563",
    labelColour: isLightTheme ? "#374151" : "#e5e7eb"
  };
};

const getBookingLabelColor = (
  labelId: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): { backgroundColour: string; labelColour: string } => {
  const allLabels = getAllBookingLabels({ venueConfig, venueGroupConfig });
  const label = allLabels.find((l) => l.id === labelId);
  const isLightTheme = isLightThemeActive();

  if (!label?.colour) {
    return getDefaultLabelColour(labelId, isLightTheme);
  }

  const baseColor = `#${label.colour}`;

  if (isLightTheme && isYellowRange(parseInt(label.colour, 16))) {
    return { backgroundColour: "#fbbf24", labelColour: "#451a03" };
  }

  return {
    backgroundColour: baseColor,
    labelColour: isLightTheme ? "#000000" : "#ffffff"
  };
};

const getCustomerLabelColor = (
  labelId: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): { backgroundColour: string; labelColour: string } => {
  const allLabels = getAllCustomerLabels({ venueConfig, venueGroupConfig });
  const label = allLabels.find((l) => l.id === labelId);

  if (label?.colour) {
    const isLightTheme =
      document.documentElement.getAttribute("data-theme") === "Light";
    return {
      backgroundColour: `#${label.colour}`,
      labelColour: isLightTheme ? "#000000" : "#ffffff"
    };
  }

  const isLightTheme =
    document.documentElement.getAttribute("data-theme") === "Light";
  return {
    backgroundColour: isLightTheme ? "#d1fae5" : "#065f46",
    labelColour: isLightTheme ? "#065f46" : "#d1fae5"
  };
};

interface Pill {
  key: string;
  label: string;
  backgroundColour: string;
  labelColour: string;
}

const formatLabelText = (text: string): string =>
  text.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const buildBookingLabelPills = (
  labels: string[],
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): Pill[] =>
  labels
    .filter((label) => label !== "linked_booking")
    .map((label, index) => {
      const isLinkedEnum = label === BookingLabelsEnum.LinkedBooking;
      const labelId = isLinkedEnum ? "linked_booking" : label;
      const displayLabel = isLinkedEnum ? "Linked" : formatLabelText(label);
      const colors = getBookingLabelColor(
        labelId,
        venueConfig,
        venueGroupConfig
      );

      return {
        key: `label-${index}`,
        label: displayLabel,
        backgroundColour: colors.backgroundColour,
        labelColour: colors.labelColour
      };
    });

const buildCustomerLabelPills = (
  customerLabels: string[],
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): Pill[] =>
  customerLabels.map((customerLabel, index) => {
    const colors = getCustomerLabelColor(
      customerLabel,
      venueConfig,
      venueGroupConfig
    );
    return {
      key: `customer-label-${index}`,
      label: formatLabelText(customerLabel),
      backgroundColour: colors.backgroundColour,
      labelColour: colors.labelColour
    };
  });

interface SpecialPillDefinition {
  key: string;
  label: string;
  condition: (booking: any) => boolean;
  lightBg: string;
  darkBg: string;
  lightFg: string;
  darkFg: string;
}

const specialPillDefinitions: SpecialPillDefinition[] = [
  {
    key: "double-confirmed",
    label: "Double Confirmed",
    condition: (booking) => Boolean(booking.doubleConfirmed),
    lightBg: "#ddd6fe",
    darkBg: "#5b21b6",
    lightFg: "#5b21b6",
    darkFg: "#ddd6fe"
  },
  {
    key: "linked-bookings",
    label: "Linked",
    condition: (booking) => (booking.linkedBookings?.length ?? 0) !== 0,
    lightBg: "#bfdbfe",
    darkBg: "#1e40af",
    lightFg: "#1e40af",
    darkFg: "#bfdbfe"
  },
  {
    key: "preorders",
    label: "Pre-orders",
    condition: (booking) => Boolean(booking.hasPreorders),
    lightBg: "#e5e7eb",
    darkBg: "#6b7280",
    lightFg: "#374151",
    darkFg: "#ffffff"
  },
  {
    key: "deposits",
    label: "Deposits",
    condition: (booking) => (booking.deposits?.length ?? 0) !== 0,
    lightBg: "#e5e7eb",
    darkBg: "#6b7280",
    lightFg: "#374151",
    darkFg: "#ffffff"
  },
  {
    key: "company",
    label: "Company",
    condition: (booking) => Boolean(booking.company?.[0]),
    lightBg: "#e5e7eb",
    darkBg: "#4b5563",
    lightFg: "#374151",
    darkFg: "#e5e7eb"
  }
];

const buildSpecialPills = (booking: any): Pill[] => {
  const isLightTheme = isLightThemeActive();

  return specialPillDefinitions
    .filter((def) => def.condition(booking))
    .map((def) => ({
      key: def.key,
      label: def.label,
      backgroundColour: isLightTheme ? def.lightBg : def.darkBg,
      labelColour: isLightTheme ? def.lightFg : def.darkFg
    }));
};

export const BookingLabels: React.FC<BookingLabelsProps> = ({
  booking,
  venueConfig,
  venueGroupConfig,
  maxPills,
  isOnRunSheet = false
}) => {
  if (isOnRunSheet) {
    return <PillsWithOverflow pills={[]} maxPills={maxPills} />;
  }

  const labelPills = buildBookingLabelPills(
    booking.labels ?? [],
    venueConfig,
    venueGroupConfig
  );
  const customerPills = buildCustomerLabelPills(
    booking.customer_labels ?? [],
    venueConfig,
    venueGroupConfig
  );
  const specialPills = buildSpecialPills(booking);
  const pills = [...labelPills, ...customerPills, ...specialPills];

  return (
    <div className="DiarySidebarListItem__labels">
      <PillsWithOverflow pills={pills} maxHeight={120} maxPills={maxPills} />
    </div>
  );
};
